import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, PaperPlaneTilt, CheckCircle, Clock, Copy, Check, PencilSimple, Plus, BellRinging, FunnelSimple, TrendUp, Stethoscope, PushPin, Trash, DownloadSimple, WarningCircle, CaretDown, ArrowClockwise, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import SendDialog from "@/components/SendDialog";
import PatientForm from "@/components/PatientForm";
import { useAccessGate } from "@/hooks/useAccessGate";
import HeaderCard from "./patientDetail/HeaderCard";
import EvolutionSection from "./patientDetail/EvolutionSection";
import { fmtDate, fmtDateTime, scoreLabel } from "./patientDetail/utils";

function buildTimeline(assignments, responses) {
  const rspById = Object.fromEntries((responses || []).map((r) => [r.assignment_id, r]));
  const events = [];
  for (const a of assignments || []) {
    // Superseded sends are hidden entirely (patient answered a sibling link,
    // so these duplicates would be noise in the timeline).
    if (a.status === "superseded") continue;
    if (a.sent_at) events.push({ kind: "sent", at: a.sent_at, assignment: a });
    if (a.reminder_sent_at) events.push({ kind: "reminder", at: a.reminder_sent_at, assignment: a });
    if (a.completed_at) events.push({ kind: "completed", at: a.completed_at, assignment: a, response: rspById[a.assignment_id] });
  }
  events.sort((x, y) => (y.at || "").localeCompare(x.at || ""));
  return events;
}

export default function PatientRecordDetail() {
  const { patientId } = useParams();
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = (currentUser?.role || "admin") === "admin";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sendOpen, setSendOpen] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [resending, setResending] = useState(null);
  const [filterTemplate, setFilterTemplate] = useState("all");
  const [hideReminders, setHideReminders] = useState(false);
  const [doneSort, setDoneSort] = useState("date-desc");
  const [directFillOpen, setDirectFillOpen] = useState(false);
  const [directTplId, setDirectTplId] = useState("");
  const [directBusy, setDirectBusy] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [pendingDeleteAsg, setPendingDeleteAsg] = useState(null); // assignment to delete
  const [deletingAsg, setDeletingAsg] = useState(false);
  const [exportingRgpd, setExportingRgpd] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { gateProps } = useAccessGate();

  // Collapsible sections — user-controlled visibility, persisted across visits.
  const [sectionsOpen, setSectionsOpen] = useState(() => {
    try {
      const raw = localStorage.getItem("qm.patient.sections");
      if (raw) return { pending: true, evolution: true, done: true, timeline: true, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { pending: true, evolution: true, done: true, timeline: true };
  });
  const toggleSection = (key) => setSectionsOpen((s) => {
    const next = { ...s, [key]: !s[key] };
    try { localStorage.setItem("qm.patient.sections", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const setAllSections = (open) => {
    const next = { pending: open, evolution: open, done: open, timeline: open };
    setSectionsOpen(next);
    try { localStorage.setItem("qm.patient.sections", JSON.stringify(next)); } catch { /* ignore */ }
  };
  const anySectionOpen = sectionsOpen.pending || sectionsOpen.evolution || sectionsOpen.done || sectionsOpen.timeline;

  useEffect(() => {
    if (directFillOpen && availableTemplates.length === 0) {
      api.get("/templates")
        // Direct-fill (in-consult) uses only the practitioner's own templates,
        // mirroring the SendDialog picker — the validated library shouldn't
        // appear here to keep the flow focused on personalised questionnaires.
        // Pinned templates float to the top so the practitioner reaches their
        // most-used questionnaires in one click.
        .then(({ data }) => setAvailableTemplates(
          data
            .filter((t) => !t.is_validated)
            .sort((a, b) => {
              if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
              return (b.updated_at || "").localeCompare(a.updated_at || "");
            })
        ))
        .catch(() => {});
    }
  }, [directFillOpen, availableTemplates.length]);

  const launchDirectFill = async () => {
    if (!directTplId) return;
    setDirectBusy(true);
    try {
      const { data: res } = await api.post(`/templates/${directTplId}/direct-fill`, { patient_id: patient.patient_id });
      window.open(`/q/${res.token}`, "_blank", "noopener,noreferrer");
      toast.success("Questionnaire ouvert dans un nouvel onglet");
      setDirectFillOpen(false);
      setDirectTplId("");
      // Refresh after 1s to let the practitioner start filling
      setTimeout(() => load(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le questionnaire");
    } finally { setDirectBusy(false); }
  };

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get(`/patient-records/${patientId}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Patient introuvable");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };
  useEffect(() => { load({ silent: true }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientId]);

  if (error) {
    return (
      <Protected>
        <AppShell title="Patient introuvable" actions={<Button variant="outline" className="rounded-md" onClick={() => nav("/patient-records")}><ArrowLeft size={16} className="mr-1.5" />Retour</Button>}>
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
            <p className="text-[#5F758F]">{error}</p>
          </div>
        </AppShell>
      </Protected>
    );
  }
  if (!data) return null;

  const { patient, assignments } = data;
  const pending = assignments.filter((a) => a.status === "sent" || a.status === "scheduled");
  const doneRaw = assignments.filter((a) => a.status === "completed");
  const responsesByAsg = new Map((data.responses || []).map((r) => [r.assignment_id, r]));
  const scorePct = (a) => {
    const r = responsesByAsg.get(a.assignment_id);
    if (!r?.score) return null;
    if (r.score.percentage != null) return r.score.percentage;
    if (r.score.max) return (r.score.total / r.score.max) * 100;
    return null;
  };
  const done = [...doneRaw].sort((a, b) => {
    if (doneSort === "date-desc") return new Date(b.completed_at || 0) - new Date(a.completed_at || 0);
    if (doneSort === "date-asc") return new Date(a.completed_at || 0) - new Date(b.completed_at || 0);
    if (doneSort === "score-desc") return (scorePct(b) ?? -1) - (scorePct(a) ?? -1);
    if (doneSort === "score-asc") return (scorePct(a) ?? Infinity) - (scorePct(b) ?? Infinity);
    return 0;
  });

  // Longitudinal evolution — group completed responses by template_id, keep only
  // series with >= 2 points (single data point is meaningless as a trend).
  // Displayed on an ABSOLUTE score scale (Y-axis = 0..max of the questionnaire)
  // rather than a normalised %, so the practitioner sees the real clinical
  // score. Responses that only carry a `percentage` (no total/max) are skipped
  // from the chart — they'd break the comparability.
  const evolutionSeries = (() => {
    const byTpl = new Map();
    for (const a of doneRaw) {
      const rsp = responsesByAsg.get(a.assignment_id);
      const sc = rsp?.score;
      if (!sc || sc.total == null || !sc.max) continue;
      const key = a.template_id;
      if (!byTpl.has(key)) byTpl.set(key, { template_id: key, title: a.template_title, is_deleted: a.template_is_deleted, max: sc.max, points: [] });
      // If subsequent responses have a higher max (rare — template edit), grow
      // the series max so all points remain in-range.
      if (sc.max > byTpl.get(key).max) byTpl.get(key).max = sc.max;
      byTpl.get(key).points.push({
        date: new Date(a.completed_at || rsp.submitted_at).getTime(),
        dateLabel: new Date(a.completed_at || rsp.submitted_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" }),
        score: sc.total,
        max: sc.max,
        raw: `${sc.total}/${sc.max}`,
        interpretation: sc.interpretation,
      });
    }
    return [...byTpl.values()]
      .filter((s) => s.points.length >= 2)
      .map((s) => ({ ...s, points: s.points.sort((a, b) => a.date - b.date) }));
  })();
  const timeline = buildTimeline(assignments, data.responses);
  const templateOptions = Array.from(
    new Map(timeline.map((ev) => [ev.assignment.template_id, ev.assignment.template_title])).entries()
  ).map(([id, title]) => ({ id, title }));
  const filteredTimeline = timeline.filter((ev) => {
    if (hideReminders && ev.kind === "reminder") return false;
    if (filterTemplate !== "all" && ev.assignment.template_id !== filterTemplate) return false;
    return true;
  });

  const resend = async (asg) => {
    setResending(asg.assignment_id);
    try {
      await api.post(`/assignments/${asg.assignment_id}/resend`);
      toast.success(`Renvoyé à ${asg.patient_email}`);
      load();
    } catch (e) {
      const data = e?.response?.data;
      // Defensive: Cloudflare may swap origin 5xx bodies with its own English
      // gateway payload. Detect it and force the French fallback.
      const isCfPayload = data?.type?.startsWith?.("https://developers.cloudflare.com")
        || /Bad gateway|invalid or incomplete response to Cloudflare/i.test(data?.detail || "")
        || /Bad gateway|Error 5\d\d/i.test(data?.title || "");
      const msg = isCfPayload
        ? "L'envoi email est temporairement indisponible. Copiez le lien et envoyez-le manuellement au patient."
        : (data?.detail || "Impossible d'envoyer l'email. Copiez le lien et envoyez-le manuellement.");
      toast.error(msg);
    } finally { setResending(null); }
  };

  const copyLink = async (asg) => {
    const url = `${window.location.origin}/q/${asg.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(asg.assignment_id);
      toast.success("Lien copié");
      setTimeout(() => setCopiedId(null), 1500);
    } catch { toast.error("Copie impossible"); }
  };

  const deletePendingAsg = async () => {
    if (!pendingDeleteAsg) return;
    const wasScheduled = pendingDeleteAsg.status === "scheduled";
    setDeletingAsg(true);
    try {
      await api.delete(`/assignments/${pendingDeleteAsg.assignment_id}`);
      toast.success(wasScheduled ? "Envoi programmé annulé" : "Envoi supprimé");
      setPendingDeleteAsg(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (wasScheduled ? "Annulation impossible" : "Suppression impossible"));
    } finally { setDeletingAsg(false); }
  };

  const exportRgpd = async () => {
    setExportingRgpd(true);
    try {
      const { data } = await api.get(`/patient-records/${patientId}/export-rgpd`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (patient.name || patient.last_name || "patient").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `questiomed-rgpd-${slug}-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export RGPD téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export impossible");
    } finally { setExportingRgpd(false); }
  };

  return (
    <Protected>
      <AppShell
        title="Patient"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-[10px] font-semibold" onClick={() => nav("/patient-records")}>
              <ArrowLeft size={16} className="mr-1.5" /> Retour
            </Button>
            <Button
              variant="outline"
              onClick={() => load()}
              disabled={refreshing}
              className="rounded-[10px] font-semibold group disabled:opacity-50 disabled:cursor-wait"
              data-testid="refresh-patient-btn"
              title="Rafraîchir"
            >
              <ArrowClockwise size={14} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : "transition-transform group-hover:rotate-45"}`} />
              Rafraîchir
            </Button>
            {isAdmin && (
              <Button variant="outline" className="rounded-[10px] font-semibold" onClick={exportRgpd} disabled={exportingRgpd} data-testid="export-rgpd-btn" title="Exporter les données du patient au format JSON (RGPD Art. 20)">
                <DownloadSimple size={16} className="mr-1.5" /> {exportingRgpd ? "Export…" : "Export RGPD"}
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-[10px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="edit-patient"
              {...gateProps({ onClick: () => setEditOpen(true), title: "Modifier" })}
            >
              <PencilSimple size={16} className="mr-1.5" /> Modifier
            </Button>
            <Button
              variant="outline"
              className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="direct-fill-btn"
              {...gateProps({ onClick: () => setDirectFillOpen(true), title: "Remplir en consultation" })}
            >
              <Stethoscope size={16} className="mr-1.5" /> Remplir en consultation
            </Button>
            <Button
              className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="new-send-btn"
              {...gateProps({ onClick: () => setSendOpen({ template_id: null }), title: "Envoyer un questionnaire" })}
            >
              <PaperPlaneTilt size={16} className="mr-1.5" weight="duotone" /> Envoyer un questionnaire
            </Button>
          </div>
        }
      >
        <HeaderCard patient={patient} pendingCount={pending.length} doneCount={done.length} />

        {/* Global collapse toggle — quick way to fold/unfold every section at once */}
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => setAllSections(!anySectionOpen)}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.08em] uppercase font-semibold text-[#5F758F] hover:text-[#1E3A5F] transition-colors"
            data-testid="toggle-all-sections"
            aria-label={anySectionOpen ? "Replier toutes les sections" : "Déplier toutes les sections"}
          >
            <CaretDown size={12} weight="bold" className={`transition-transform ${anySectionOpen ? "" : "-rotate-90"}`} />
            {anySectionOpen ? "Tout replier" : "Tout déplier"}
          </button>
        </div>

        {/* Pending — actionable, kept at the top */}
        {pending.length > 0 && (
          <div className="mb-8">
            <button
              type="button"
              onClick={() => toggleSection("pending")}
              aria-expanded={sectionsOpen.pending}
              className="flex items-center gap-3 mb-4 w-full text-left"
              data-testid="toggle-pending"
            >
              <CaretDown size={14} weight="bold" className={`text-[#5F758F] transition-transform ${sectionsOpen.pending ? "" : "-rotate-90"}`} />
              <Clock size={20} weight="duotone" className="text-[#D45A3F]" />
              <h3 className="text-xl font-bold text-[#1E3A5F]">En attente</h3>
              <span className="bg-[#FFF3D6] text-[#D45A3F] text-[13px] px-2.5 py-0.5 rounded-full font-bold border border-[#F0DDA8]">{pending.length}</span>
            </button>
            {sectionsOpen.pending && (
            <div className="flex flex-col gap-2.5">
              {pending.map((a) => {
                const draftCount = typeof a.draft_answered_count === "number"
                  ? a.draft_answered_count
                  : (a.draft_answers ? Object.keys(a.draft_answers).length : 0);
                const isInProgress = !!a.draft_saved_at && draftCount > 0;
                const qTotal = a.template_question_count || 0;
                const progressPct = qTotal > 0 ? Math.min(100, Math.round((draftCount / qTotal) * 100)) : 0;
                return (
                <div
                  key={a.assignment_id}
                  className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 grid grid-cols-[1fr_auto_auto] gap-4 items-center transition-all hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)]"
                  data-testid={`pending-${a.assignment_id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[17px] font-semibold text-[#1E3A5F]">{a.template_title}</p>
                      {a.template_is_deleted && (
                        <span className="text-[#D45A3F] text-[13px] font-medium" data-testid={`tpl-deleted-${a.assignment_id}`}>
                          Questionnaire supprimé — lien invalide
                        </span>
                      )}
                    </div>
                    <p className="text-[14px] text-[#5F758F] mt-1">
                      {a.status === "scheduled" && a.scheduled_for ? (
                        <>Programmé pour le {new Date(a.scheduled_for).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                      ) : a.sent_at ? (
                        <>Envoyé le {new Date(a.sent_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                      ) : null}
                      {a.last_resend_at && a.status !== "scheduled" && (
                        <> · {a.resend_count || 1} relance{(a.resend_count || 1) > 1 ? "s" : ""}, dernière le {new Date(a.last_resend_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                      )}
                    </p>
                    {isInProgress && (
                      <div className="mt-2 flex items-center gap-2.5" data-testid={`draft-progress-${a.assignment_id}`}>
                        <div className="h-1.5 flex-1 max-w-[240px] rounded-full bg-[#E1E9F0] overflow-hidden">
                          <div className="h-full bg-[#3A8FA5] transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                        <span className="text-[12px] text-[#3A8FA5] font-semibold tabular-nums">
                          {draftCount}{qTotal > 0 ? ` / ${qTotal}` : ""} répondue{draftCount > 1 ? "s" : ""}
                          {a.draft_saved_at && (
                            <> · dernière activité {new Date(a.draft_saved_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {a.status === "scheduled" ? (
                    <span className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#7A4EA8] text-[#7A4EA8] bg-[#F2E7F5]" data-testid={`badge-scheduled-${a.assignment_id}`}>
                      <Calendar size={12} weight="fill" /> Programmé
                    </span>
                  ) : isInProgress ? (
                    <span className="inline-flex items-center justify-center px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#3A8FA5] text-[#3A8FA5] bg-[#E6F1F5]" data-testid={`badge-in-progress-${a.assignment_id}`}>
                      En cours de réponse
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center px-3.5 py-1.5 text-[12px] rounded-full font-semibold uppercase tracking-[0.05em] border border-[#D45A3F] text-[#D45A3F]">
                      En attente
                    </span>
                  )}
                  <div className="flex gap-2 shrink-0">
                    {a.status === "scheduled" ? (
                      <span className="text-[12px] text-[#5F758F] italic self-center px-3">Sera envoyé automatiquement</span>
                    ) : a.template_is_deleted ? (
                      <span className="text-[12px] text-[#5F758F] italic self-center px-3">Renvoi impossible</span>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" className="rounded-[10px] border-[#E1E9F0] font-semibold px-4 text-[14px] h-10" onClick={() => copyLink(a)} data-testid={`copy-${a.assignment_id}`}>
                          {copiedId === a.assignment_id ? <Check size={16} /> : <Copy size={16} />}
                          <span className="ml-1.5">Copier</span>
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold px-4 text-[14px] h-10 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`resend-${a.assignment_id}`}
                          {...gateProps({
                            onClick: () => resend(a),
                            disabled: resending === a.assignment_id || !a.patient_email,
                            title: a.patient_email ? "Renvoyer par email" : "Pas d'email — copiez le lien",
                          })}
                        >
                          <PaperPlaneTilt size={16} className="mr-1.5" />
                          {resending === a.assignment_id ? "…" : "Renvoyer"}
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC] px-3 h-10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`delete-pending-${a.assignment_id}`}
                      {...gateProps({
                        onClick: () => setPendingDeleteAsg(a),
                        title: a.status === "scheduled" ? "Annuler cet envoi programmé" : "Supprimer cet envoi en attente",
                      })}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              );})}
            </div>
            )}
          </div>
        )}

        <EvolutionSection
          series={evolutionSeries}
          open={sectionsOpen.evolution}
          onToggle={() => toggleSection("evolution")}
        />

        {/* Completed questionnaires — quick access to responses */}
        {done.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <button
                type="button"
                onClick={() => toggleSection("done")}
                aria-expanded={sectionsOpen.done}
                className="flex items-center gap-3 text-left"
                data-testid="toggle-done"
              >
                <CaretDown size={14} weight="bold" className={`text-[#5F758F] transition-transform ${sectionsOpen.done ? "" : "-rotate-90"}`} />
                <CheckCircle size={20} weight="duotone" className="text-[#3A8FA5]" />
                <h3 className="text-xl font-bold text-[#1E3A5F]">Questionnaires complétés</h3>
                <span className="bg-[#E6F1F5] text-[#3A8FA5] text-[13px] px-2.5 py-0.5 rounded-full font-bold">{done.length}</span>
              </button>
              <div className="ml-auto">
                <Select value={doneSort} onValueChange={setDoneSort}>
                  <SelectTrigger className="h-9 w-[220px] bg-white border-[#E1E9F0] rounded-[10px] text-[13px]" data-testid="done-sort-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date-desc">Plus récent d'abord</SelectItem>
                    <SelectItem value="date-asc">Plus ancien d'abord</SelectItem>
                    <SelectItem value="score-desc">Score le plus haut</SelectItem>
                    <SelectItem value="score-asc">Score le plus bas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {sectionsOpen.done && (
            <div className="flex flex-col gap-2.5">
              {done.map((a) => {
                const rsp = (data.responses || []).find((r) => r.assignment_id === a.assignment_id);
                const subs = rsp?.score?.subscores || [];
                const subTone = (interp) => {
                  const s = (interp || "").toLowerCase();
                  if (/sévère|severe|important|critique/.test(s)) return "crit";
                  if (/modér|moder|léger|leger/.test(s)) return "warn";
                  return "normal";
                };
                const rowInner = (
                  <div className="grid grid-cols-[1fr_auto] gap-6 items-start">
                    <div className="min-w-0">
                      <p className="text-[17px] font-semibold text-[#1E3A5F] mb-1">
                        {a.template_title}
                        {a.filled_by_practitioner && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6] px-2 py-0.5 text-[10px] tracking-[0.12em] uppercase font-bold align-middle">
                            Consultation
                          </span>
                        )}
                        {a.template_is_deleted && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[9px] tracking-[0.14em] uppercase font-semibold italic align-middle" data-testid={`done-deleted-${a.assignment_id}`}>
                            Supprimé
                          </span>
                        )}
                      </p>
                      <p className="text-[14px] text-[#5F758F]">Complété le {new Date(a.completed_at || rsp?.submitted_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    {rsp?.score && (
                      <div className="flex flex-col items-end gap-2.5 shrink-0">
                        <div className="flex items-center gap-3">
                          {rsp.score.interpretation && (
                            <span className="text-[12px] font-semibold px-3 py-1 rounded-full bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6] whitespace-nowrap" data-testid={`done-interp-${a.assignment_id}`}>
                              {rsp.score.interpretation}
                            </span>
                          )}
                          <p className="text-[28px] font-bold text-[#1E3A5F] leading-none tabular-nums">{scoreLabel(rsp.score)}</p>
                        </div>
                        {subs.length > 0 && (
                          <div className="flex flex-wrap justify-end gap-1.5 max-w-[380px]" data-testid={`subs-${a.assignment_id}`}>
                            {subs.map((s) => {
                              const tone = subTone(s.interpretation);
                              const cls = tone === "crit"
                                ? "bg-[#FFE0DC] border-[#EEC1BB] text-[#8B2A1E]"
                                : tone === "warn"
                                  ? "bg-[#FFF3D6] border-[#F0DDA8] text-[#1E3A5F]"
                                  : "bg-[#EEF3F8] border-[#E1E9F0] text-[#1E3A5F]";
                              const iCls = tone === "crit" ? "text-[#8B2A1E] font-semibold" : "text-[#D45A3F]";
                              return (
                                <span key={s.key} className={`inline-flex items-baseline gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium border whitespace-nowrap ${cls}`}>
                                  <span className={tone === "normal" ? "text-[#5F758F]" : ""}>{s.label}</span>
                                  <span className="font-bold tabular-nums">{s.total}/{s.max}</span>
                                  {s.interpretation && <span className={`text-[12px] ${iCls}`}>· {s.interpretation}</span>}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
                const base = "block bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 transition-all";
                if (a.response_id) {
                  return (
                    <Link
                      key={a.assignment_id}
                      to={`/responses/${a.response_id}`}
                      className={`${base} cursor-pointer hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)]`}
                      data-testid={`done-${a.assignment_id}`}
                    >
                      {rowInner}
                    </Link>
                  );
                }
                return (
                  <div key={a.assignment_id} className={base} data-testid={`done-${a.assignment_id}`}>
                    {rowInner}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* Chronological Timeline */}
        <div className="bg-white border border-[#E1E9F0] rounded-2xl">
          <div className={`px-6 py-4 flex flex-wrap items-center gap-3 ${sectionsOpen.timeline ? "border-b border-[#E1E9F0]" : ""}`}>
            <button
              type="button"
              onClick={() => toggleSection("timeline")}
              aria-expanded={sectionsOpen.timeline}
              className="flex items-center gap-3 text-left"
              data-testid="toggle-timeline"
            >
              <CaretDown size={14} weight="bold" className={`text-[#5F758F] transition-transform ${sectionsOpen.timeline ? "" : "-rotate-90"}`} />
              <div className="w-5 h-5 rounded-full border-2 border-[#3A8FA5] flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3A8FA5]" />
              </div>
              <h3 className="font-serif text-xl">Chronologie</h3>
              <Badge className="bg-[#EEF3F8] text-[#3A8FA5] hover:bg-[#EEF3F8]">{filteredTimeline.length}{filteredTimeline.length !== timeline.length && <span className="opacity-60"> / {timeline.length}</span>}</Badge>
            </button>
            {sectionsOpen.timeline && timeline.length > 0 && (
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <FunnelSimple size={16} weight="duotone" className="text-[#5F758F]" />
                <Select value={filterTemplate} onValueChange={setFilterTemplate}>
                  <SelectTrigger className="h-9 w-[240px] bg-white border-[#E1E9F0] text-sm" data-testid="timeline-filter-template">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les questionnaires</SelectItem>
                    {templateOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setHideReminders((v) => !v)}
                  className={`h-9 px-3 rounded-md border text-xs tracking-[0.14em] uppercase font-semibold transition-colors ${hideReminders ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#5F758F] border-[#E1E9F0] hover:border-[#B7C4D6]"}`}
                  data-testid="timeline-toggle-reminders"
                  title={hideReminders ? "Afficher les rappels" : "Masquer les rappels"}
                >
                  {hideReminders ? "Rappels masqués" : "Masquer rappels"}
                </button>
                {(filterTemplate !== "all" || hideReminders) && (
                  <button
                    type="button"
                    onClick={() => { setFilterTemplate("all"); setHideReminders(false); }}
                    className="text-xs text-[#3A8FA5] hover:text-[#164E5F] underline underline-offset-2"
                    data-testid="timeline-clear-filters"
                  >Réinitialiser</button>
                )}
              </div>
            )}
          </div>
          {sectionsOpen.timeline && (filteredTimeline.length === 0 ? (
            <p className="p-8 text-center text-[#5F758F]">
              {timeline.length === 0
                ? "Aucun événement pour le moment. Envoyez un premier questionnaire à ce patient."
                : "Aucun événement ne correspond aux filtres actifs."}
            </p>
          ) : (
            <ol className="relative px-6 py-6" data-testid="patient-timeline">
              <div className="absolute left-[38px] top-6 bottom-6 w-px bg-[#E1E9F0]" />
              {filteredTimeline.map((ev, i) => (
                <TimelineNode
                  key={`${ev.assignment.assignment_id}-${ev.kind}-${i}`}
                  event={ev}
                  copiedId={copiedId}
                  onCopy={copyLink}
                  onResend={resend}
                  resending={resending}
                  gateProps={gateProps}
                />
              ))}
            </ol>
          ))}
        </div>

        {sendOpen && (
          <SendDialogPatientPre
            patient={patient}
            onClose={() => { setSendOpen(null); load(); }}
          />
        )}

        <Dialog open={directFillOpen} onOpenChange={(v) => { if (!v && !directBusy) { setDirectFillOpen(false); setDirectTplId(""); } }}>
          <DialogContent className="bg-white sm:max-w-md">
            <DialogHeader>
              <div className="w-12 h-12 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center mb-2">
                <Stethoscope size={22} weight="duotone" className="text-[#3A8FA5]" />
              </div>
              <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Remplir en consultation</DialogTitle>
              <DialogDescription className="text-[#5F758F] leading-relaxed">
                Vous remplissez pour <strong className="text-[#1E3A5F]">{patient.first_name} {patient.last_name}</strong>. Aucun email ne sera envoyé, le score sera enregistré dans son dossier.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Questionnaire</Label>
              <Select value={directTplId} onValueChange={setDirectTplId}>
                <SelectTrigger className="mt-2 bg-white border-[#E1E9F0] rounded-[10px]" data-testid="direct-fill-template">
                  <SelectValue placeholder="Choisir un questionnaire…" />
                </SelectTrigger>
                <SelectContent>
                  {availableTemplates.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-[#5F758F] text-center">Chargement…</div>
                  ) : availableTemplates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      <span className="inline-flex items-center gap-2">
                        {t.is_pinned && <PushPin size={13} weight="fill" className="text-[#3A8FA5] shrink-0" />}
                        <span>{t.title}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-[10px] font-semibold" onClick={() => { setDirectFillOpen(false); setDirectTplId(""); }} disabled={directBusy}>Annuler</Button>
              <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={launchDirectFill} disabled={!directTplId || directBusy} data-testid="direct-fill-launch">
                {directBusy ? "Ouverture…" : "Ouvrir le questionnaire"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PatientForm
          open={editOpen}
          initial={patient}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />

        <AlertDialog open={!!pendingDeleteAsg} onOpenChange={(v) => { if (!v && !deletingAsg) setPendingDeleteAsg(null); }}>
          <AlertDialogContent className="bg-white" data-testid="delete-pending-dialog">
            <AlertDialogHeader>
              <div className={`mx-auto sm:mx-0 mb-2 w-12 h-12 rounded-[14px] flex items-center justify-center border ${pendingDeleteAsg?.status === "scheduled" ? "bg-[#F2E7F5] border-[#D4B8DE]" : "bg-[#FFE0DC] border-[#EEC1BB]"}`}>
                <WarningCircle size={26} weight="duotone" className={pendingDeleteAsg?.status === "scheduled" ? "text-[#7A4EA8]" : "text-[#8B2A1E]"} />
              </div>
              <AlertDialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">
                {pendingDeleteAsg?.status === "scheduled" ? "Annuler cet envoi programmé ?" : "Supprimer cet envoi ?"}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#5F758F] leading-relaxed">
                {pendingDeleteAsg?.status === "scheduled" ? (
                  <>
                    L'envoi <strong className="text-[#1E3A5F]">« {pendingDeleteAsg?.template_title} »</strong> était programmé{pendingDeleteAsg?.scheduled_for ? <> pour le <strong className="text-[#1E3A5F]">{new Date(pendingDeleteAsg.scheduled_for).toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong></> : ""}. Il n'a pas encore été envoyé.
                    <span className="block mt-3 text-xs">
                      En annulant, l'email ne sera pas envoyé et aucune trace ne sera conservée dans la fiche du patient.
                    </span>
                  </>
                ) : (
                  <>
                    Vous êtes sur le point de supprimer l'envoi <strong className="text-[#1E3A5F]">« {pendingDeleteAsg?.template_title} »</strong>. Le lien envoyé au patient deviendra invalide.
                    <span className="block mt-3 text-xs">
                      {pendingDeleteAsg?.draft_saved_at
                        ? "Attention : ce patient a commencé à répondre — son brouillon sera perdu. "
                        : ""}
                      Les réponses <strong>déjà complétées et enregistrées</strong> (section Réponses) ne sont jamais affectées par cette action.
                    </span>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-[10px]" disabled={deletingAsg}>Retour</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); deletePendingAsg(); }}
                disabled={deletingAsg}
                className={`rounded-[10px] text-white font-semibold ${pendingDeleteAsg?.status === "scheduled" ? "bg-[#7A4EA8] hover:bg-[#5E3B84]" : "bg-[#8B2A1E] hover:bg-[#6D2117]"}`}
                data-testid="delete-pending-confirm"
              >
                {deletingAsg
                  ? (pendingDeleteAsg?.status === "scheduled" ? "Annulation…" : "Suppression…")
                  : (pendingDeleteAsg?.status === "scheduled" ? "Annuler l'envoi" : "Supprimer l'envoi")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppShell>
    </Protected>
  );
}

/**
 * Timeline node — renders one chronological event (sent, reminder, completed).
 */
function TimelineNode({ event, copiedId, onCopy, onResend, resending, gateProps }) {  const a = event.assignment;
  const rsp = event.response;
  const config = {
    sent: {
      icon: PaperPlaneTilt,
      color: "text-[#3A8FA5]",
      dot: "bg-[#3A8FA5] border-[#B7C4D6]",
      label: "Envoyé",
    },
    reminder: {
      icon: BellRinging,
      color: "text-[#D45A3F]",
      dot: "bg-white border-[#D45A3F]",
      label: "Rappel envoyé",
    },
    completed: {
      icon: CheckCircle,
      color: "text-[#3A8FA5]",
      dot: "bg-[#3A8FA5] border-[#3A8FA5]",
      label: "Réponse reçue",
    },
  }[event.kind];
  const Icon = config.icon;

  return (
    <li className="relative pl-14 pb-8 last:pb-0" data-testid={`timeline-${event.kind}-${a.assignment_id}`}>
      <span className={`absolute left-4 top-1 w-4 h-4 rounded-full border-2 ${config.dot}`} />
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md hover:bg-[#F4F7FB] px-3 py-2 -mx-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={16} weight="duotone" className={config.color} />
            <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#5F758F]">{config.label}</p>
          </div>
          <p className="text-[#1E3A5F] font-medium mt-1.5">{a.template_title}
            {a.template_is_deleted && (
              <span className="ml-2 text-[#D45A3F] text-xs font-medium" data-testid={`timeline-deleted-${a.assignment_id}`}>
                Questionnaire supprimé — lien invalide
              </span>
            )}
          </p>
          <p className="text-xs text-[#5F758F] mt-0.5">{fmtDateTime(event.at)}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {event.kind === "completed" && rsp?.score && (
            <div className="text-right">
              <p className="text-[10px] tracking-widest uppercase text-[#5F758F]">Score</p>
              <p className="font-serif text-lg text-[#1E3A5F] leading-tight">{scoreLabel(rsp.score)}</p>
            </div>
          )}
          {event.kind === "completed" && a.response_id && (
            <Link to={`/responses/${a.response_id}`}>
              <Button size="sm" variant="outline" className="rounded-md" data-testid={`timeline-view-${a.assignment_id}`}>Voir</Button>
            </Link>
          )}
          {event.kind === "sent" && a.status !== "completed" && !a.template_is_deleted && (
            <>
              <Button size="sm" variant="outline" className="rounded-md" onClick={() => onCopy(a)} data-testid={`timeline-copy-${a.assignment_id}`}>
                {copiedId === a.assignment_id ? <Check size={14} /> : <Copy size={14} />}
              </Button>
              <Button
                size="sm"
                className="rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`timeline-resend-${a.assignment_id}`}
                {...gateProps({
                  onClick: () => onResend(a),
                  disabled: resending === a.assignment_id || !a.patient_email,
                  title: a.patient_email ? "Renvoyer par email" : "Pas d'email — copiez le lien",
                })}
              >
                <PaperPlaneTilt size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Small wrapper: opens the shared SendDialog with a template picker step
 * before selecting the patient (already known).
 */
function SendDialogPatientPre({ patient, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/templates");
      // Patient send: only practitioner's own templates (exclude validated library)
      const mine = data.filter((t) => !t.is_validated).sort((a, b) => {
        if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
      setTemplates(mine);
    })();
  }, []);
  if (picked) {
    // Reuse SendDialog with pre-selected patient
    return <SendDialog template={picked} presetPatientId={patient.patient_id} onClose={onClose} />;
  }
  const pinnedCount = templates.filter((t) => t.is_pinned).length;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl mb-4">Choisir un questionnaire</h3>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#E1E9F0] -mx-6">
          {templates.map((t, idx) => {
            const isFirstUnpinned = pinnedCount > 0 && !t.is_pinned && (idx === 0 || templates[idx - 1]?.is_pinned);
            return (
              <div key={t.template_id}>
                {isFirstUnpinned && (
                  <p className="px-6 py-1.5 text-[10px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold bg-[#F4F7FB]">Autres questionnaires</p>
                )}
                <button
                  onClick={() => setPicked(t)}
                  className="w-full px-6 py-3 flex items-center justify-between hover:bg-[#F4F7FB] text-left"
                  data-testid={`pick-${t.template_id}`}
                >
                  <span className="text-[#1E3A5F] flex items-center gap-2 min-w-0">
                    {t.is_pinned && <PushPin size={13} weight="fill" className="text-[#3A8FA5] shrink-0" />}
                    <span className="truncate">{t.title}</span>
                  </span>
                  <span className="text-xs text-[#5F758F] shrink-0 ml-2">{t.is_validated ? "Bibliothèque" : ""}</span>
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="outline" className="rounded-md" onClick={onClose}>Annuler</Button>
        </div>
      </div>
    </div>
  );
}
